import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';
import { MaskService } from '../../../../shared/services/mask.service';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LinhaProdutoService } from '../../../../shared/services/apis/produto/linha-produto.service';
import { LinhaProdutoFaixaComissaoRequest, LinhaProdutoRequest } from '../../../../shared/services/requests/produto/linha-produto.request';

@Component({
    selector: 'app-linha-produto-cadastro',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        RouterModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatDialogModule,
        MatCheckboxModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        NgxCurrencyDirective,
        HeaderComponent,
        LoadingBoxComponent,
        FooterActionComponent,
    ],
    templateUrl: './linha-produto-cadastro.component.html',
    styleUrl: './linha-produto-cadastro.component.css'
})
export class LinhaProdutoCadastroComponent implements OnInit {

    public moduloEnum = ModuloEnum.Configuracoes;
    public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Cadastros;

    public form: FormGroup;
    public isLoading: boolean = false;
    public isSaving: boolean = false;
    public isEdicao: boolean = false;
    public idLinhaProduto: number = 0;

    constructor(
        private formBuilder: FormBuilder,
        private linhaProdutoService: LinhaProdutoService,
        private alertService: AlertService,
        private router: Router,
        private route: ActivatedRoute,
        private maskService: MaskService
    ) { }

    public percentMask = this.maskService.getPercentMask();

    ngOnInit(): void {
        this.createForm();

        this.route.params.subscribe(params => {
            if (params['idLinhaProduto']) {
                this.idLinhaProduto = +params['idLinhaProduto'];
                this.isEdicao = true;
                this.carregarLinhaProduto();
            }
        });
    }

    private createForm(): void {
        this.form = this.formBuilder.group({
            descricao: ['', [Validators.required, Validators.maxLength(100)]],
            comissaoFixa: [true],
            comissaoConformeDescontos: [false],
            percentualComissao: [null, [Validators.min(0)]],
            faixasComissao: this.formBuilder.array([])
        });

        this.form.get('comissaoFixa')?.valueChanges.subscribe(checked => {
            if (checked) {
                this.form.get('comissaoConformeDescontos')?.setValue(false, { emitEvent: false });
                this.form.get('percentualComissao')?.enable();
            } else {
                this.form.get('percentualComissao')?.disable();
                this.form.get('percentualComissao')?.setValue(null);
            }
        });

        this.form.get('comissaoConformeDescontos')?.valueChanges.subscribe(checked => {
            if (checked) {
                this.form.get('comissaoFixa')?.setValue(false, { emitEvent: false });
                this.form.get('percentualComissao')?.disable();
                this.form.get('percentualComissao')?.setValue(null);
                if (this.faixasArray.length === 0) {
                    this.addFaixaRow();
                }
            }
        });
    }

    public get faixasArray(): FormArray {
        return this.form.get('faixasComissao') as FormArray;
    }

    private createFaixaRow(descontoAte: number | null = null, comissao: number | null = null): FormGroup {
        return this.formBuilder.group({
            percentualDesconto: [descontoAte, Validators.required],
            percentualComissao: [comissao, Validators.required],
        });
    }

    public addFaixaRow(): void {
        this.faixasArray.push(this.createFaixaRow());
    }

    public removeFaixaRow(index: number): void {
        this.faixasArray.removeAt(index);
    }

    private carregarLinhaProduto(): void {
        this.isLoading = true;
        this.linhaProdutoService.getById(this.idLinhaProduto).subscribe({
            next: (res) => {
                this.isLoading = false;
                if (res.isError) {
                    this.alertService.show('Atenção', res.errorDescription || 'Erro ao carregar linha de produto.', AlertType.warning);
                    this.voltar();
                    return;
                }

                const lp = res.linhaProduto;
                if (!lp) return;

                const isComissaoDescontos = !lp.isComissaoFixa && (lp.listaFaixasComissao?.length ?? 0) > 0;

                // Patch sem emitir events para não interferir nos listeners dos checkboxes
                this.form.patchValue({
                    descricao: lp.descricao,
                    comissaoFixa: lp.isComissaoFixa,
                    comissaoConformeDescontos: isComissaoDescontos,
                    percentualComissao: lp.percentualComissaoFixa ?? null
                }, { emitEvent: false });

                // Controla habilitação do campo de percentual manualmente
                if (lp.isComissaoFixa) {
                    this.form.get('percentualComissao')?.enable({ emitEvent: false });
                } else {
                    this.form.get('percentualComissao')?.disable({ emitEvent: false });
                }

                // Carrega as faixas de desconto, sempre em ordem crescente de desconto
                if (isComissaoDescontos && lp.listaFaixasComissao?.length) {
                    [...lp.listaFaixasComissao]
                        .sort((a, b) => (a.percentualDescontoAte ?? 0) - (b.percentualDescontoAte ?? 0))
                        .forEach(f => {
                            this.faixasArray.push(this.createFaixaRow(f.percentualDescontoAte, f.percentualComissao));
                        });
                }
            },
            error: (err) => {
                this.isLoading = false;
                this.alertService.show('Erro', 'Ocorreu um erro ao carregar a linha de produto.', AlertType.error);
                console.error(err);
                this.voltar();
            }
        });
    }


    public get isComissaoFixa(): boolean {
        return this.form.get('comissaoFixa')?.value === true;
    }

    public get isComissaoDescontos(): boolean {
        return this.form.get('comissaoConformeDescontos')?.value === true;
    }

    public salvar(): void {
        this.form.markAllAsTouched();

        if (this.form.get('descricao')?.hasError('required')) {
            this.alertService.show('Atenção', 'A descrição é obrigatória.', AlertType.warning);
            return;
        }
        if (this.form.get('descricao')?.hasError('maxlength')) {
            this.alertService.show('Atenção', 'A descrição deve ter no máximo 100 caracteres.', AlertType.warning);
            return;
        }
        if (!this.isComissaoFixa && !this.isComissaoDescontos) {
            this.alertService.show('Atenção', 'Selecione pelo menos um tipo de comissão.', AlertType.warning);
            return;
        }
        if (this.isComissaoFixa && Number(this.form.get('percentualComissao')?.value) < 0) {
            this.alertService.show('Atenção', 'Comissão (%) deve ser maior ou igual a zero.', AlertType.warning);
            return;
        }
        if (this.isComissaoDescontos && !this.validarFaixasComissao()) {
            return;
        }

        const isComissaoFixa = this.isComissaoFixa;
        const isComissaoDescontos = this.isComissaoDescontos;

        let listaFaixas: LinhaProdutoFaixaComissaoRequest[] = [];

        if (isComissaoDescontos) {
            listaFaixas = this.faixasArray.value
                .map((item: { percentualDesconto: number; percentualComissao: number }) => ({
                    percentualDescontoAte: Number(item.percentualDesconto),
                    percentualComissao: Number(item.percentualComissao)
                }))
                .sort((a: LinhaProdutoFaixaComissaoRequest, b: LinhaProdutoFaixaComissaoRequest) =>
                    a.percentualDescontoAte - b.percentualDescontoAte);
        }

        const request: LinhaProdutoRequest = {
            descricao: this.form.get('descricao')?.value,
            isComissaoFixa: isComissaoFixa,
            percentualComissaoFixa: isComissaoFixa ? (this.form.get('percentualComissao')?.value ?? null) : null,
            listaFaixasComissao: isComissaoDescontos ? listaFaixas : []
        };

        this.isSaving = true;

        if (this.isEdicao) {
            this.linhaProdutoService.update(this.idLinhaProduto, request).subscribe({
                next: (res) => this.tratarRetornoSalvar(res),
                error: (err) => this.tratarErroSalvar(err)
            });
        } else {
            this.linhaProdutoService.create(request).subscribe({
                next: (res) => this.tratarRetornoSalvar(res),
                error: (err) => this.tratarErroSalvar(err)
            });
        }
    }

    private tratarRetornoSalvar(res: any): void {
        this.isSaving = false;
        if (res.isError) {
            this.alertService.show('Atenção', res.errorDescription || 'Erro ao salvar linha de produto.', AlertType.warning);
        } else {
            this.alertService.show('Sucesso', 'Linha de produto salva com sucesso.', AlertType.success);
            this.voltar();
        }
    }

    private tratarErroSalvar(err: any): void {
        this.isSaving = false;
        this.alertService.show('Erro', 'Ocorreu um erro ao salvar.', AlertType.error);
        console.error(err);
    }

    public voltar(): void {
        this.router.navigate(['/configuracoes/cadastros/linha-produtos']);
    }

    private validarFaixasComissao(): boolean {
        if (this.faixasArray.length === 0) {
            this.alertService.show('Atenção', 'Para comissão variável, informe ao menos uma faixa de comissão.', AlertType.warning);
            return false;
        }

        const descontos: number[] = [];

        for (let i = 0; i < this.faixasArray.length; i++) {
            const item = this.faixasArray.at(i).value;
            const linhaTexto = `na faixa ${i + 1}`;

            const temDesconto = item.percentualDesconto !== null && item.percentualDesconto !== undefined && item.percentualDesconto.toString() !== '';
            const temComissao = item.percentualComissao !== null && item.percentualComissao !== undefined && item.percentualComissao.toString() !== '';

            if (!temDesconto || !temComissao) {
                this.alertService.show('Atenção', `Preencha desconto e comissão ${linhaTexto}.`, AlertType.warning);
                return false;
            }

            const desconto = Number(item.percentualDesconto);
            const comissao = Number(item.percentualComissao);

            if (!Number.isFinite(desconto) || desconto <= 0) {
                this.alertService.show('Atenção', `Desconto de até (%) deve ser maior que zero ${linhaTexto}.`, AlertType.warning);
                return false;
            }

            if (!Number.isFinite(comissao) || comissao < 0) {
                this.alertService.show('Atenção', `Comissão (%) deve ser maior ou igual a zero ${linhaTexto}.`, AlertType.warning);
                return false;
            }

            descontos.push(desconto);
        }

        // Valida duplicatas (a ordem não importa — as faixas são ordenadas ao salvar)
        if (new Set(descontos).size !== descontos.length) {
            this.alertService.show('Atenção', 'Não é permitido repetir "Desconto de até (%)" nas faixas.', AlertType.warning);
            return false;
        }

        return true;
    }
}
